import { Component, computed, inject, Input, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

import { DateFormatPipe } from "../../../pipes/date-format.pipe";
import { SchemaStatusPipe } from "../../../pipes/schema-status.pipe";
import { Schema } from '../../../models/schema.model';
import { calculatePaginationPages } from '../../../helpers/pagination-helper';
import { MediaDirective } from '../../../directives/media-directive';
import { DatetimeFormatPipe } from "../../../pipes/datetime-format.pipe";
import { toSignal } from '@angular/core/rxjs-interop';
import { SchemaRepository } from '../../../repositories/schema.repository';
import { SchemaStatus, SchemaTabs } from '../../../enums/schema-status';

@Component({
    selector: 'patient-schema-table',
    imports: [CommonModule, TranslocoDirective, DateFormatPipe, SchemaStatusPipe, MediaDirective, DatetimeFormatPipe],
    templateUrl: './patient-schema-table.component.html'
})
export class PatientSchemaTableComponent implements OnInit {
    constructor(private translocoService: TranslocoService) { }
    private readonly router = inject(Router);
    private readonly schemaStore = inject(SchemaRepository);
    
    readonly otherSchemas: Signal<Schema[]> = toSignal(this.schemaStore.patientOtherSchemas$, { initialValue: [] });
    readonly hasOthersSchemaAccess: Signal<boolean> = computed(() => this.otherSchemas().length > 0);
    readonly SchemaTabs = SchemaTabs;

    @Input() paginatedSchemas$!: Observable<Schema[]>;
    @Input() schemas$!: Observable<Schema[]>;
    @Input() currentPage: number = 1;
    @Input() lastPage!: number;
    @Input() perPage!: number;
    @Input() pages!: number;
    @Input() visiblePaginationPages!: number[];
    @Input() pagesIntervalSize!: number;
    @Input() currentTab!: SchemaTabs;
    @Input() tabKey!: string;
    
    sortColumn$ = new BehaviorSubject<keyof Schema | null>(null);
    sortDirection$ = new BehaviorSubject<'asc' | 'desc'>('asc');
    SchemaStatus = SchemaStatus;

    ngOnInit() {
        this.visiblePaginationPages = calculatePaginationPages(this.currentPage, this.lastPage, this.pages, this.pagesIntervalSize);
        
        const savedPage = sessionStorage.getItem(this.tabKey + 'schemaTablePage');
        this.currentPage = savedPage ? +savedPage : 1;
        this.updatePage();
    }

    changePage(page: number): void {
        if (page < 1 || page > this.lastPage) return;

        this.currentPage = page;
        sessionStorage.setItem(this.tabKey + 'schemaTablePage', page.toString());
        this.updatePage();
    }

    updatePage(){
        this.paginatedSchemas$ = combineLatest([
            this.schemas$,
            this.sortColumn$,
            this.sortDirection$
        ]).pipe(
            map(([schemas, sortColumn, sortDirection]) => {
            let sorted = [...schemas];

            if (sortColumn) {
                const translations = this.translocoService.translateObject("schema") as Record<string, string>;
                sorted.sort((a: any, b: any) => {
                const aValue = sortColumn == "schemaStatus" ?
                            translations[a[sortColumn].toLowerCase()] :
                            a[sortColumn];
                const bValue =  sortColumn == "schemaStatus" ?
                            translations[b[sortColumn].toLowerCase()] :
                            b[sortColumn];

                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
                if (bValue == null) return sortDirection === 'asc' ? 1 : -1;

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
                }

                return sortDirection === 'asc'
                    ? aValue > bValue ? 1 : -1
                    : aValue < bValue ? 1 : -1;
                });
            }

            // Paginate
            const start = (this.currentPage - 1) * this.perPage;
            const end = this.currentPage * this.perPage;
            return sorted.slice(start, end);
            })
        );
        this.updatePaginationBar();
        this.onChangeScrollTo()
    }

    onSchemaClicked(schemaId: number, sentCount: number, schemaStatus: string): void {
        if(sentCount > 0 || schemaStatus != SchemaStatus.EXPIRED) {
            this.router.navigate(["schema", schemaId]);
        }
    }

    private updatePaginationBar(): void {
        this.visiblePaginationPages = calculatePaginationPages(this.currentPage, this.lastPage, this.pages, this.pagesIntervalSize);
    }

    private onChangeScrollTo() {
        const element = document.querySelector(".scroll-to");
        element?.scrollIntoView({ behavior: "instant" });
    }

    sortBy(column: keyof Schema) {
        if (this.sortColumn$.value === column) {
            const newDir = this.sortDirection$.value === 'asc' ? 'desc' : 'asc';
            this.sortDirection$.next(newDir);
        } else {
            this.sortColumn$.next(column);
            this.sortDirection$.next('asc');
        }

        this.currentPage = 1;
        sessionStorage.setItem(this.tabKey + 'schemaTablePage', this.currentPage.toString());
        this.updatePage();
    }
}

